import React from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Heading, Text } from 'rebass';
import Loading from 'app/loading';
import Button from 'app/buttons';
import { UnassignedCard, CardGrid, CardTitle } from '../styled';
import { useUnassignedBookEntitlements, useUnassignedAddOnEntitlements } from '../hooks';

// Shows the customer what they just bought, before they continue into the
// allocation flow on the dashboard.
//
// This reads the *unassigned* entitlement lists rather than an order-scoped
// endpoint: at this point in the flow nothing has been allocated yet (journal
// creation happens on dashboard entry, which is the next step), so unassigned ==
// everything purchased. It also means a customer who abandons here and returns
// later still sees a truthful list rather than a stale order snapshot.
const PurchaseSummary = ({ onContinue }) => {
  const { entitlements: books, isLoading: booksLoading, error: booksError } =
    useUnassignedBookEntitlements();
  const { entitlements: addOns, isLoading: addOnsLoading, error: addOnsError } =
    useUnassignedAddOnEntitlements();

  if (booksLoading || addOnsLoading) return <Loading local />;

  const error = booksError || addOnsError;
  const label = entitlement => entitlement.bundleName || entitlement.description || 'Baby book';

  return (
    <Box px={[3, 4]} py={4} maxWidth="1200px" mx="auto">
      <Heading fontFamily="Poppins" fontSize={['24px', '32px']} mb={2}>
        Thanks for your purchase!
      </Heading>
      <Text color="#64748B" fontSize="14px" mb={4}>
        Here&rsquo;s what you bought. Next you&rsquo;ll choose who each book is for.
      </Text>

      {error && (
        <Text color="red" fontFamily="Poppins" fontSize="14px" mb={3}>
          We couldn&rsquo;t load your purchase details. You can continue and review them on your
          account.
        </Text>
      )}

      {!!books.length && (
        <Box mb={4}>
          <Heading fontFamily="Poppins" fontSize="18px" mb={2}>
            {books.length === 1 ? '1 baby book' : `${books.length} baby books`}
          </Heading>
          <CardGrid>
            {books.map(entitlement => (
              <UnassignedCard key={entitlement.id}>
                {/* Full bundle name is shown verbatim — not shortened. */}
                <CardTitle>{label(entitlement)}</CardTitle>
              </UnassignedCard>
            ))}
          </CardGrid>
        </Box>
      )}

      {!!addOns.length && (
        <Box mb={4}>
          <Heading fontFamily="Poppins" fontSize="18px" mb={2}>
            Add-ons &amp; upgrades
          </Heading>
          <CardGrid>
            {addOns.map(entitlement => (
              <UnassignedCard key={entitlement.id}>
                <CardTitle>{label(entitlement)}</CardTitle>
              </UnassignedCard>
            ))}
          </CardGrid>
        </Box>
      )}

      {!books.length && !addOns.length && !error && (
        <Text color="#64748B" fontSize="14px" mb={4}>
          Everything from this order has already been set up.
        </Text>
      )}

      <Flex>
        <Button onClick={onContinue}>Continue</Button>
      </Flex>
    </Box>
  );
};

PurchaseSummary.propTypes = {
  onContinue: PropTypes.func.isRequired,
};

export default PurchaseSummary;
